/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import jsPDF from "jspdf"; // Biblioteca para PDF
import * as XLSX from "xlsx"; // Biblioteca para Excel
import axios from "axios"; // Para buscar dados do backend
// import autoTable from "jspdf-autotable";
import { useHistoryContext } from "../context/HistoryContext"; // Conexão com o histórico
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip,
} from "chart.js";
import { useThemeContext } from "../components/GeneralConfig"; // Para acessar darkMode
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import safeLogger from "../utils/safeLogger";

// Gera nome de arquivo seguro a partir de base e data atual
const makeFilename = (base) => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yy = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const display = `${base} - ${dd}/${mm}/${yy} - ${hh}:${min}`;
  const filenameSafe = `${base} - ${dd}-${mm}-${yy} - ${hh}.${min}`;
  return { display, filenameSafe };
};

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Title,
  Tooltip
);

const ExportResults = () => {
  const { history, getAnalysisCount } = useHistoryContext(); // Pega os dados do histórico
  const [format, setFormat] = useState("");
  const { darkMode } = useThemeContext(); // Obtém o estado do tema
  const [userName, setUserName] = useState("NOME DO USUÁRIO");
  const [userMatricula, setUserMatricula] = useState(
    "MATRÍCULA NÃO DISPONÍVEL"
  );
  const [userEmpresa, setUserEmpresa] = useState("");

  // Função para buscar os dados do perfil do usuário
  const fetchUserProfile = async () => {
    try {
      const response = await axios.get("https://codefort.onrender.com/api/profile", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      safeLogger.info({ event: "profile_fetch", data: safeLogger.mask(response.data) });
      const data = response.data || {};
      setUserName(data.name || data.fullName || data.userName || "NOME DO USUÁRIO");

      // Verifica campos de matrícula e empresa com fallbacks
      const registration = data.registration || data.matricula || data.registrationNumber || data.employeeId || "";
      setUserMatricula(registration || "MATRÍCULA NÃO DISPONÍVEL");
      setUserEmpresa((data.company && data.company.name) || data.companyName || "");
    } catch (error) {
      safeLogger.error("Erro ao buscar perfil do usuário:", error.message);
    }
  };

  useEffect(() => {
    fetchUserProfile(); // Busca o perfil ao carregar o componente
  }, []);

  const handleExport = async () => {
    if (!userName || userName === "NOME DO USUÁRIO") {
      try {
        await fetchUserProfile(); // Garante que os dados do usuário sejam buscados
      } catch (error) {
        alert("Erro ao carregar os dados do usuário. Tente novamente.");
        return;
      }
    }

    if (format === "excel") {
      exportToExcel(history);
    } else if (format === "pdf") {
      exportToPDF(history);
    } else {
      alert("Selecione um formato para exportar.");
    }
  };

  const exportToExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Histórico de Análises");

    // Estilização do cabeçalho
    const headerStyle = {
      font: { bold: true, color: { argb: "FFFFFFFF" } },
      fill: {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1F4E79" },
      },
      alignment: { horizontal: "center", vertical: "middle" },
    };

    const defaultBorder = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };

    // Estrutura da tabela de informações do usuário
  let userInfoTable = [["Nome", userName]];

  if (userMatricula) {
    userInfoTable.push(["Matrícula", userMatricula]);
  }

  if (userEmpresa) {
    userInfoTable.push(["Empresa", userEmpresa]);
  }

    userInfoTable.forEach((row, index) => {
      const rowIndex = index + 1;
      worksheet.addRow(row);
      worksheet.getCell(`A${rowIndex}`).font = { bold: true };
      worksheet.getCell(`A${rowIndex}`).alignment = { horizontal: "right" };
      worksheet.getCell(`B${rowIndex}`).alignment = { horizontal: "left" };

      worksheet.getCell(`A${rowIndex}`).border = defaultBorder;
      worksheet.getCell(`B${rowIndex}`).border = defaultBorder;
    });

    // Adicionar linha vazia
    worksheet.addRow([]);

    // Cabeçalho da tabela de dados
    const dataHeaderRow = worksheet.addRow(["Data", "Tipo", "Detalhes"]);
    dataHeaderRow.eachCell((cell) => {
      cell.style = headerStyle;
      cell.border = defaultBorder;
    });

    // Adicionar dados na tabela
    data.forEach((row) => {
      const newRow = worksheet.addRow([row.date, row.type, row.details]);
      newRow.eachCell((cell) => {
        cell.border = defaultBorder;
      });
    });

    // Ajustar largura das colunas
    worksheet.columns = [
      { width: 25 }, // Data
      { width: 20 }, // Tipo
      { width: 50 }, // Detalhes
    ];

    // Baixar o arquivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const { filenameSafe } = makeFilename("resultados_formatados");
    saveAs(blob, `${filenameSafe}.xlsx`);
  };

  // Exportação para PDF com gráficos
  const exportToPDF = (data) => {
    const doc = new jsPDF();

    // Cabeçalho
    // const userName = user?.name || "NOME DO USUÁRIO"; // Nome do usuário
    // const userMatricula = user?.matricula || "MATRÍCULA NÃO DISPONÍVEL"; // Matrícula do usuário
    const exportDate = new Date().toLocaleString("pt-BR"); // Data e hora da exportação

    doc.setFont("helvetica", "bold"); // Fonte em negrito
    doc.setFontSize(12);
    doc.text(`Nome: ${userName}`, 10, 10);
    doc.text(`Matrícula: ${userMatricula}`, 10, 20);
    doc.text(`Data de Exportação: ${exportDate}`, 10, 30);

    // Ajustar contagem precisa por categoria
    const validData = data.filter(
      (d) =>
        d &&
        typeof d.type === "string" &&
        d.type &&
        d.details &&
        (d.type === "CodeAnalysis" || d.type.includes("Testes Unitários") || ["Cypress", "Postman"].includes(d.type))
    );    
    const usageSummary = {
      Cypress: validData.filter((d) => d.type === "Cypress").length,
      Postman: validData.filter((d) => d.type === "Postman").length,
      JavaScript: validData.filter((d) => d.type?.includes("JavaScript"))
        .length,
      Java: validData.filter((d) => d.type?.includes("Java")).length,
      Python: validData.filter((d) => d.type?.includes("Python")).length,
      CSharp: validData.filter((d) => d.type?.includes("C#")).length,
      Analysis: validData.filter((d) => d.type === "CodeAnalysis").length,
    };

    // Gráfico
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 300;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext("2d");
    new Chart(ctx, {
      type: "bar",
      data: {
        labels: Object.keys(usageSummary), // Inclui "Analysis"
        datasets: [
          {
            label: "Uso por Categoria",
            data: Object.values(usageSummary), // Inclui contagem de "Analysis"
            backgroundColor: [
              "#FF6384", // Cypress
              "#36A2EB", // Postman
              "#FFCE56", // JavaScript
              "#4BC0C0", // Java
              "#9966FF", // Python
              "#FF9F40", // C#
              "#8BC34A", // Analysis
            ],
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            labels: {
              font: {
                weight: "bold",
              },
            },
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: "Categorias",
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Quantidade de Análises",
            },
          },
        },
      },
    });

    // Adicionar gráfico ao PDF
    setTimeout(() => {
      try {
        const imgData = canvas.toDataURL("image/png");
        doc.addImage(imgData, "PNG", 10, 40, 180, 90); // Ajustar altura do gráfico para não sobrepor
        document.body.removeChild(canvas); // Remove o canvas
      } catch (error) {
        alert("Erro ao gerar o gráfico. Tente novamente.");
        document.body.removeChild(canvas); // Remove o canvas em caso de erro
        return;
      }

      // Adicionar legenda estilizada
      doc.setFontSize(12);
      const startY = 140; // Ajuste para iniciar abaixo do gráfico
      const legenda = [
        `Cypress - Automação de Teste${usageSummary.Cypress === 1 ? "" : "s"}`,
        `Postman - Automação de Teste${usageSummary.Postman === 1 ? "" : "s"}`,
        `JavaScript - Teste Unitário${
          usageSummary.JavaScript === 1 ? "" : "s"
        }`,
        `Java - Teste Unitário${usageSummary.Java === 1 ? "" : "s"}`,
        `Python - Teste Unitário${usageSummary.Python === 1 ? "" : "s"}`,
        `CSharp - Teste Unitário${usageSummary.CSharp === 1 ? "" : "s"}`,
        `Analysis - Análise de Código${usageSummary.Analysis === 1 ? "" : "s"}`,
      ];

      Object.entries(usageSummary).forEach(([key, value], index) => {
        doc.setFont("helvetica", "bold"); // Define fonte em negrito
        doc.setTextColor(0, 0, 0); // Define texto normal preto
        doc.text(`${key} - `, 10, startY + index * 10);

        doc.setTextColor(0, 128, 0); // Define cor verde para o número
        doc.text(`${value}`, 40, startY + index * 10);

        doc.setTextColor(0, 0, 0); // Volta ao preto para o restante do texto
        doc.text(` ${legenda[index].split(" - ")[1]}`, 50, startY + index * 10);
      });

      // Rodapé
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold"); // Negrito no rodapé
        doc.text(
          `Relatório gerado automaticamente pela CodeFort.`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 15,
          { align: "center" }
        );
        doc.text(
          `Todos os direitos reservados.`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }

      // Salvar PDF
      const { filenameSafe } = makeFilename("relatorio_uso");
      doc.save(`${filenameSafe}.pdf`);
    }, 500);
  };

  return (
    <Container
      sx={{
        backgroundColor: darkMode ? "#121212" : "#ffffff",
        color: darkMode ? "#ffffff" : "#000000",
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Exportar Resultados
        </Typography>
        <Typography variant="body1" gutterBottom>
          Escolha o formato e baixe o histórico de análises realizadas.
        </Typography>
        <FormControl
          fullWidth
          margin="normal"
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: "2px",
              borderColor: darkMode ? "#ffffff" : "#1d4f91",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderWidth: "2px",
                borderColor: darkMode ? "#90caf9" : "#1d4f91",
              },
            "& .MuiInputLabel-root": {
              backgroundColor: darkMode ? "#121212" : "#ffffff",
              padding: "0 4px",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: darkMode ? "#90caf9" : "#1d4f91",
            },
          }}
        >
          <InputLabel id="format-select-label">Formato</InputLabel>
          <Select
            labelId="format-select-label"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Selecione o formato" }}
            sx={{
              backgroundColor: darkMode ? "#1d1d1d" : "#ffffff",
              color: darkMode ? "#ffffff" : "#000000",
            }}
          >
            <MenuItem value="excel">Excel (.xlsx)</MenuItem>
            <MenuItem value="pdf">PDF</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          sx={{
            mt: 2,
            backgroundColor: darkMode ? "#90caf9" : "#1d4f91",
            color: darkMode ? "#000000" : "#ffffff",
            "&:hover": {
              backgroundColor: darkMode ? "#64b5f6" : "#154b8b",
            },
          }}
          disabled={!format}
        >
          Exportar
        </Button>
      </Box>
    </Container>
  );
};

export default ExportResults;
